import {
  type StudySwimlaneSelectDto,
  type StudyThreadSelectDto,
  StudyThreadSelectDtoKillChainCategoryEnum,
} from '@@types/api.types';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useDeleteMeiaThreadSwimlane, useUpdateMeiaThreadSwimlane } from '@queries/meia.query';
import { type MeiaSwimlaneFormValues, meiaSwimlaneSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import { Trash2Icon } from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';

type MeiaSwimlaneStageFormChildren = (params: {
  /** Delete control for custom-thread stages; `undefined` for templated stages. */
  deleteButton: ReactNode;
  /** The stage form fields to render inside the host's body/content area. */
  formBody: ReactNode;
  /** Reads the form's current dirty state — for host close-guards. */
  getIsDirty: () => boolean;
  isUpdating: boolean;
  /** Persists the step; rejects if the save mutation fails. */
  submit: () => Promise<void>;
  /** Surfaces this step's validation errors without saving; returns step validity. */
  validate: () => boolean;
}) => ReactNode;

type MeiaSwimlaneStageFormProps = {
  canEdit: boolean;
  children: MeiaSwimlaneStageFormChildren;
  /** Called after the stage is deleted; hosts close themselves here. */
  onDeleted: () => void;
  /**
   * Called after a successful save. Single-step hosts (the standalone stage modal)
   * pass `onClose` to close on save. Multi-step hosts omit it and instead drive
   * closing themselves via `submit()` so both steps persist before the modal closes.
   */
  onSaved?: () => void;
  /**
   * Suppress this form's own success toast so a multi-step host can show a single
   * toast for the whole save. Defaults to false (the standalone modal toasts).
   */
  silentSuccess?: boolean;
  studyId: string;
  swimlane: StudySwimlaneSelectDto;
  thread: StudyThreadSelectDto;
};

/**
 * Stage editing form (operational purpose, entry/exit criteria, locked/rename
 * note) shared by the standalone `MeiaSwimlaneModal` and the P2 thread-stage
 * modal's Details tab. The render-prop hands back the submit handler, pending
 * flag, and delete control so the host owns the surrounding chrome/footer.
 */
export const MeiaSwimlaneStageForm: FC<MeiaSwimlaneStageFormProps> = (props) => {
  const { canEdit, children, onDeleted, onSaved, silentSuccess = false, studyId, swimlane, thread } = props;
  const isCustomThread = thread.killChainCategory === StudyThreadSelectDtoKillChainCategoryEnum.CUSTOM;
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const { isPending: isUpdating, mutateAsync: updateSwimlane } = useUpdateMeiaThreadSwimlane();
  const { mutateAsync: deleteSwimlane } = useDeleteMeiaThreadSwimlane();

  const defaultValues: MeiaSwimlaneFormValues = {
    entryCriteria: swimlane.entryCriteria ?? '',
    exitCriteria: swimlane.exitCriteria ?? '',
    operationalPurpose: swimlane.operationalPurpose ?? '',
    phaseName: swimlane.phaseName,
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      try {
        await updateSwimlane({
          entryCriteria: value.entryCriteria,
          exitCriteria: value.exitCriteria,
          operationalPurpose: value.operationalPurpose,
          phaseName: isCustomThread ? value.phaseName : undefined,
          studyId,
          swimlaneId: swimlane.id,
          threadId: thread.id,
        });
      } catch (error) {
        // Re-throw so multi-step hosts awaiting submit() keep the modal open on failure.
        errorToast({ description: 'Failed to update stage.' });
        throw error;
      }
      // Rebase the baseline so hosts that stay open don't see a stale dirty flag.
      form.reset(value);
      if (!silentSuccess) successToast({ description: 'Stage updated.' });
      onSaved?.();
    },
    onSubmitInvalid: createFormInvalidHandler<typeof defaultValues>(),
    validators: { onSubmit: meiaSwimlaneSchema },
  });

  const handleConfirmDelete = async (): Promise<void> => {
    await deleteSwimlane({ studyId, swimlaneId: swimlane.id, threadId: thread.id });
    successToast({ description: 'Stage deleted.' });
    onDeleted();
  };

  const submit = (): Promise<void> => form.handleSubmit();

  const validate = (): boolean => {
    // safeParse yields the synchronous validity the host's Save / step-nav needs; the
    // async validateAllFields only paints the inline field errors and is fire-and-forget.
    const isValid = meiaSwimlaneSchema.safeParse(form.state.values).success;
    if (!isValid) void form.validateAllFields('submit');
    return isValid;
  };
  const deleteButton =
    canEdit && isCustomThread ? (
      <Button
        onClick={() => setIsConfirmingDelete(true)}
        type="button"
        variant="destructive"
      >
        <Trash2Icon className="size-4" />
        Delete
      </Button>
    ) : undefined;

  const formBody = (
    <form
      {...MEIA_AUTOFILL_IGNORE_PROPS}
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        submit();
      }}
    >
      <form.AppForm>
        {isCustomThread ? (
          <form.AppField name="phaseName">
            {(field) => (
              <field.TextField
                {...MEIA_AUTOFILL_IGNORE_PROPS}
                disabled={!canEdit}
                label="Stage Name"
                placeholder="e.g., Detect"
                required
              />
            )}
          </form.AppField>
        ) : (
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            Stage <span className="font-semibold text-gray-800">{swimlane.phaseName}</span> is locked because the Thread
            uses a templated kill chain. Switch the Thread to <span className="font-semibold">Custom</span> to rename or
            reorder Stages.
          </div>
        )}
        <form.AppField name="operationalPurpose">
          {(field) => (
            <field.TextAreaField
              disabled={!canEdit}
              label="Operational Purpose"
              placeholder="What outcome does this Stage produce for the Thread?"
              rows={3}
            />
          )}
        </form.AppField>
        <form.AppField name="entryCriteria">
          {(field) => (
            <field.TextAreaField
              disabled={!canEdit}
              label="Entry Criteria"
              placeholder="Conditions that must be true before entering this Stage."
              rows={3}
            />
          )}
        </form.AppField>
        <form.AppField name="exitCriteria">
          {(field) => (
            <field.TextAreaField
              disabled={!canEdit}
              label="Exit Criteria"
              placeholder="Conditions that must be true before leaving this Stage."
              rows={3}
            />
          )}
        </form.AppField>
      </form.AppForm>
    </form>
  );

  return (
    <>
      {children({ deleteButton, formBody, getIsDirty: () => form.state.isDirty, isUpdating, submit, validate })}
      <ConfirmDeleteModal
        isOpen={isConfirmingDelete}
        itemName="stage"
        onConfirm={handleConfirmDelete}
        setIsOpen={setIsConfirmingDelete}
      />
    </>
  );
};
